import { useState } from 'react';
import styled, { useTheme } from 'styled-components';

import Box from '../../../../components/Box';
import ContentHeaderStyled from '../../../../components/ContentHeader';
import Flex from '../../../../components/Flex';
import Legend from '../../../../components/Legend';
import { usePageActions } from '../../../../components/PageActions/context';
import PaginationStyled from '../../../../components/Pagination';
import RelatedPlotsStyled from '../../../../components/RelatedPlots';
import Tabs from '../../../../components/Tabs';
import Text from '../../../../components/Text';
import ZoomImage from '../../../../components/ZoomImage';
import { withChartDescriptions } from '../../chartDescriptions';
import ChartSettingsSelect from '../../components/ChartSettingsSelect';
import LibrariesSelect from '../../components/LibrariesSelect';
import { RelatedPlotsData } from '../../types';

import UsabilityTab from './components/UsabilityTab';
import UseCaseTab from './components/UseCaseTab';
import chart from './images/chart.webp';

const dataChart: RelatedPlotsData[] = withChartDescriptions([
  {
    title: 'Matrix',
    url: '/data-visualization/tables/matrix',
    image: '/images/data-viz-tables/matrix.png',
    tags: ['correlation', 'comparison'],
  },
  {
    title: 'Punchcard',
    url: '/data-visualization/tables/punchcard',
    image: '/images/data-viz-tables/punchcard.png',
    tags: ['correlation', 'comparison'],
  },
  {
    title: 'Donut Chart',
    url: '/data-visualization/circulars/donut',
    image: '/images/data-viz-circulars/donut.png',
    tags: ['proportion'],
  },
]);

const dataTabs = [
  {
    id: 'tab-use-case',
    title: 'Use Case',
    content: <UseCaseTab />,
  },
  {
    id: 'tab-usability',
    title: 'Usability',
    content: <UsabilityTab />,
  },
];

interface Props {
  className?: string;
}

type SelectData = {
  label: string;
  value: string;
  imagePath: string;
}[];

const selectData: SelectData = [
  {
    label: '',
    value: 'defaultChart',
    imagePath: chart,
  },
];

const legendData: Record<string, { label: string; color: string }[]> = {
  defaultChart: [],
  builder: [],
  excel: [],
};

type LibraryType = 'builder' | 'powerBI' | 'excel' | 'esri' | 'figma';

const activeChartSettings: Record<LibraryType, SelectData> = {
  powerBI: selectData,
  builder: selectData,
  excel: selectData,
  esri: selectData,
  figma: selectData,
};

const DEFAULT_ACTIVE_LIBRARY = 'builder';

const HeatmapStructure = ({ className = '' }: Props) => {
  const [selectedTab, setSelectedTab] = useState('tab-use-case');
  const { isFullScreen, onResetSettings } = usePageActions();
  const [activeLibrary, setActiveLibrary] = useState<LibraryType>(DEFAULT_ACTIVE_LIBRARY);
  const [activeChart, setActiveChart] = useState(activeChartSettings[activeLibrary][0].value);
  const theme = useTheme();

  const resetSettingsCallback = () => {
    setActiveLibrary(DEFAULT_ACTIVE_LIBRARY);
    setActiveChart(selectData[0].value);
  };

  const onSetActiveLibrary = (value: LibraryType) => {
    setActiveLibrary(value as LibraryType);
    setActiveChart(activeChartSettings[value as LibraryType][0].value);
  };

  onResetSettings(() => {
    resetSettingsCallback();
  });

  const activeChartData = activeChartSettings[
    activeLibrary as keyof typeof activeChartSettings
  ].find((chart) => chart.value === activeChart);

  return (
    <div className={className}>
      <div className="content">
        <ContentHeaderStyled
          style={{ marginBottom: isFullScreen ? '16px' : undefined }}
          controlBar
          title="Heatmap"
          breadcrumbs={[
            { label: 'Data Visualization', url: '/data-visualization' },
            { label: 'Tables', url: '/data-visualization/tables' },
            { label: 'Heatmap' },
          ]}
        />
        <Box
          style={{
            marginBottom: `${isFullScreen ? '0' : '40px'}`,
            border: `1px solid ${theme.neutralBorder}`,
            borderRadius: '6px',
          }}
          as="section"
        >
          {!isFullScreen && (
            <Box
              style={{
                borderBottom: `1px solid ${theme.neutralBorder}`,
                backgroundColor: theme.neutralBackground,
                padding: '12px 16px',
              }}
            >
              <Flex align="center" gap="40px" justify="space-between">
                <Text
                  color={theme.neutralTextWeak}
                  fontSize={theme.fontSizes.xs}
                  lineHeight={theme.lineHeights.xs}
                >
                  Please select a kit from the dropdown menu to change the chart’s view and access
                  the appropriate kit page. <br />
                  Follow the provided instructions to download the kit, locate your plot, and
                  complete the installation process.
                </Text>
                <LibrariesSelect
                  defaultValue={activeLibrary}
                  onSelect={(value) => onSetActiveLibrary(value as LibraryType)}
                  optionsToExclude={['esri']}
                />
              </Flex>
            </Box>
          )}
          <Box>
            <Box style={{ padding: '46px 24px 34px 24px' }}>
              <Flex justify="space-between">
                {activeChartSettings[activeLibrary][0].label ? (
                  <ChartSettingsSelect
                    defaultValue={activeChart}
                    onSelect={(val) => {
                      setActiveChart(val);
                    }}
                    placeholder="Active Chart Settings"
                    data={activeChartSettings[activeLibrary]}
                  />
                ) : (
                  <div />
                )}
                <Legend type="categorical" data={legendData[activeChart]} />
              </Flex>
            </Box>
            <ZoomImage imgSrc={activeChartData?.imagePath} />
          </Box>
        </Box>

        {!isFullScreen && (
          <>
            <Tabs
              onSelectedTab={setSelectedTab}
              className="tabs-component"
              tabs={dataTabs.map((item) => ({ ...item, content: item.content, title: item.title }))}
            />
            {selectedTab === 'tab-use-case' && (
              <RelatedPlotsStyled
                title="Related Plots"
                description="These visualizations can be used as alternatives to the Heatmap."
                data={dataChart}
                className="related-plots-section"
              />
            )}
            <PaginationStyled
              nextLink="/data-visualization/tables/matrix"
              next="Matrix"
              className="footer-pagination"
            />
          </>
        )}
      </div>
    </div>
  );
};

const StyledHeatmapStructure = styled(HeatmapStructure)`
  width: 100%;
  .content {
    margin: 0 auto;
    width: 100%;
    min-height: 100%;
    display: flex;
    flex-direction: column;

    > .tabs-component {
      margin-bottom: 40px;
    }

    > .footer-pagination {
      display: none;
      margin-top: auto;
      @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
        display: block;
      }
    }

    > .related-plots-section {
      margin-bottom: 40px;
    }
  }
`;

export default StyledHeatmapStructure;
